/**
 * useJobs Hook
 * Custom hook for managing job postings data, filters, and pagination
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  getJobPostings, 
  searchJobs, 
  getJobCategories, 
  getActiveCompanies,
  subscribeToJobPostings 
} from '../services/careerService';

/**
 * Custom hook for job postings management
 * 
 * @param {Object} initialFilters - Initial filter state
 * @param {string} searchTerm - Search term
 * @returns {Object} Jobs data and management functions
 */
const useJobs = (initialFilters = {}, searchTerm = '') => {
  // State management
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [categories, setCategories] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination settings
  const JOBS_PER_PAGE = 20;

  // Load initial jobs data
  const loadJobs = useCallback(async (filters = {}, search = '', reset = true) => {
    try {
      setLoading(true);
      setError(null);

      let jobsData;
      
      // Use search if search term is provided
      if (search.trim()) {
        jobsData = await searchJobs(search, filters);
        // Search doesn't support pagination currently
        setJobs(jobsData);
        setHasMore(false);
        setLastDoc(null);
        setTotalCount(jobsData.length);
      } else {
        // Regular filtered query with pagination
        const result = await getJobPostings(
          JOBS_PER_PAGE, 
          reset ? null : lastDoc, 
          filters
        );
        
        if (reset) {
          setJobs(result.jobs);
        } else {
          setJobs(prev => [...prev, ...result.jobs]);
        }
        
        setHasMore(result.hasMore);
        setLastDoc(result.lastDoc);
        setTotalCount(prev => reset ? result.jobs.length : prev + result.jobs.length);
      }
    } catch (err) {
      console.error('Error loading jobs:', err);
      setError(err.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [lastDoc]);

  // Load more jobs (pagination)
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    
    await loadJobs(initialFilters, searchTerm, false);
  }, [hasMore, loading, initialFilters, searchTerm, loadJobs]);

  // Refresh jobs data
  const refresh = useCallback(async () => {
    setLastDoc(null);
    await loadJobs(initialFilters, searchTerm, true);
  }, [initialFilters, searchTerm, loadJobs]);

  // Load categories and companies data
  const loadMetadata = useCallback(async () => {
    try {
      const [categoriesData, companiesData] = await Promise.all([
        getJobCategories(),
        getActiveCompanies()
      ]);
      
      setCategories(categoriesData);
      setCompanies(companiesData);
    } catch (err) {
      console.error('Error loading job metadata:', err);
      // Don't set error state for metadata failures
    }
  }, []);

  // Real-time updates subscription
  const subscribeToUpdates = useCallback((filters = {}) => {
    const unsubscribe = subscribeToJobPostings((updatedJobs) => {
      // Only update if we're on the first page and not searching
      if (!searchTerm && !lastDoc) {
        setJobs(prevJobs => {
          // Merge new jobs with existing ones, avoiding duplicates
          const existingIds = new Set(prevJobs.map(job => job.id));
          const newJobs = updatedJobs.filter(job => !existingIds.has(job.id));
          
          if (newJobs.length > 0) {
            return [...newJobs, ...prevJobs].slice(0, JOBS_PER_PAGE);
          }
          return prevJobs;
        });
      }
    }, filters);

    return unsubscribe;
  }, [searchTerm, lastDoc]);

  // Effect: Load jobs when filters or search term changes
  useEffect(() => {
    setLastDoc(null); // Reset pagination
    loadJobs(initialFilters, searchTerm, true);
  }, [initialFilters, searchTerm, loadJobs]);

  // Effect: Load metadata on mount
  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  // Effect: Subscribe to real-time updates
  useEffect(() => {
    let unsubscribe;
    
    // Only subscribe if not searching and filters are not too specific
    if (!searchTerm && Object.keys(initialFilters).length <= 2) {
      unsubscribe = subscribeToUpdates(initialFilters);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [searchTerm, initialFilters, subscribeToUpdates]);

  // Get job by ID from current jobs list
  const getJobById = useCallback((jobId) => {
    return jobs.find(job => job.id === jobId);
  }, [jobs]);

  // Filter jobs locally (for quick filtering)
  const filterJobs = useCallback((filterFn) => {
    return jobs.filter(filterFn);
  }, [jobs]);

  // Get job statistics
  const getStats = useCallback(() => {
    const stats = {
      total: totalCount,
      byType: {},
      byLocation: {},
      byExperience: {},
      urgent: 0,
      recent: 0 // Jobs posted in last 7 days
    };

    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    jobs.forEach(job => {
      // Count by job type
      stats.byType[job.jobType] = (stats.byType[job.jobType] || 0) + 1;
      
      // Count by location
      stats.byLocation[job.location] = (stats.byLocation[job.location] || 0) + 1;
      
      // Count by experience level
      stats.byExperience[job.experienceLevel] = (stats.byExperience[job.experienceLevel] || 0) + 1;
      
      // Count urgent jobs
      if (job.isUrgent) {
        stats.urgent++;
      }
      
      // Count recent jobs
      if (new Date(job.createdAt) > weekAgo) {
        stats.recent++;
      }
    });

    return stats;
  }, [jobs, totalCount]);

  // Check if filters are active
  const hasActiveFilters = useCallback(() => {
    return Object.values(initialFilters).some(filter => filter && filter.trim() !== '') || 
           searchTerm.trim() !== '';
  }, [initialFilters, searchTerm]);

  return {
    // Data
    jobs,
    categories,
    companies,
    totalCount,
    
    // State
    loading,
    error,
    hasMore,
    
    // Actions
    loadMore,
    refresh,
    
    // Utilities
    getJobById,
    filterJobs,
    getStats,
    hasActiveFilters
  };
};

export default useJobs;